import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { customFieldService, CustomProductField } from '../../services/configurationService';
import { customToast } from '../../utils/customToast';
import { Plus, Edit, ToggleLeft, ToggleRight } from 'lucide-react';
import CustomFieldModal from './CustomFieldModal';

const CustomFieldsManagement: React.FC = () => {
  const { theme } = useTheme();
  const [fields, setFields] = useState<CustomProductField[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedField, setSelectedField] = useState<CustomProductField | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      setLoading(true);
      const response = await customFieldService.getAll();
      setFields(Array.isArray(response) ? response : (response as any).data || []);
    } catch (error: any) {
      customToast.error('Failed to load custom fields');
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setSelectedField(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (field: CustomProductField) => {
    setSelectedField(field);
    setIsModalOpen(true);
  };

  const handleToggleActive = async (field: CustomProductField) => {
    try {
      await customFieldService.toggleActive(field.id);
      customToast.success(`Field ${field.is_active ? 'deactivated' : 'activated'} successfully`);
      loadFields();
    } catch (error: any) {
      customToast.error('Failed to toggle field status');
    }
  };

  const handleModalSave = () => {
    setIsModalOpen(false);
    loadFields();
  };

  const getFieldTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      text: 'Text',
      number: 'Number',
      date: 'Date',
      textarea: 'Text Area',
      select: 'Dropdown',
      checkbox: 'Checkbox'
    };
    return labels[type] || type;
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <h2 className={`text-lg sm:text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Custom Product Fields
          </h2>
          <p className={`text-xs sm:text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Add custom fields to your products for specific shop requirements
          </p>
        </div>
        <button
          onClick={handleAddClick}
          className="w-full sm:w-auto px-3 sm:px-4 py-2 sm:py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 text-sm font-medium touch-manipulation"
        >
          <Plus size={18} />
          <span>Add Field</span>
        </button>
      </div>

      {/* Fields Table */}
      {loading ? (() => {
        const skeletonColor = theme === 'dark' ? 'bg-slate-600' : 'bg-gray-300';
        return (
          <div className={`overflow-x-auto rounded-lg border ${
            theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <table className="w-full">
              <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
                <tr>
                  <th className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className={`h-4 w-24 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                    <div className={`h-4 w-20 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">
                    <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                    <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor}`} />
                  </th>
                  <th className="px-2 sm:px-4 py-2 sm:py-3 text-right">
                    <div className={`h-4 w-16 rounded animate-pulse ${skeletonColor} ml-auto`} />
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {[1, 2, 3, 4, 5].map((idx) => (
                  <tr key={idx} className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                    {/* Column 1: Field Name + Description (always visible, shows mobile badges) */}
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div>
                        {/* Field Name */}
                        <div className={`h-4 w-28 rounded animate-pulse mb-1 ${skeletonColor}`} />
                        {/* Description (optional) */}
                        <div className="space-y-1 mb-2">
                          <div className={`h-3 w-40 rounded animate-pulse ${skeletonColor}`} />
                          <div className={`h-3 w-32 rounded animate-pulse ${skeletonColor}`} />
                        </div>
                        {/* Mobile badges section - Type badge */}
                        <div className="md:hidden mb-1">
                          <div className={`h-5 w-20 rounded inline-block animate-pulse ${skeletonColor}`} />
                        </div>
                        {/* Mobile badges section - Status badge */}
                        <div className="sm:hidden">
                          <div className={`h-5 w-20 rounded-full inline-block animate-pulse ${skeletonColor}`} />
                        </div>
                      </div>
                    </td>

                    {/* Column 2: Field Type Badge (hidden on sm, show md+) */}
                    <td className="px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell">
                      <div className={`h-6 w-20 rounded animate-pulse ${skeletonColor}`} />
                    </td>

                    {/* Column 3: Required (Yes/No) (hidden on lg, show lg+) */}
                    <td className="px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell">
                      <div className={`h-4 w-8 rounded animate-pulse ${skeletonColor}`} />
                    </td>

                    {/* Column 4: Status Badge (hidden on sm, show sm+) */}
                    <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                      <div className={`h-6 w-20 rounded-full animate-pulse ${skeletonColor}`} />
                    </td>

                    {/* Column 5: Action Buttons (Edit icon + Toggle icon, both rounded-full) */}
                    <td className="px-2 sm:px-4 py-2 sm:py-3">
                      <div className="flex justify-end space-x-1 sm:space-x-2">
                        {/* Edit button (rounded-full) */}
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full animate-pulse ${skeletonColor}`} />
                        {/* Toggle button (rounded-full, green when active) */}
                        <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full animate-pulse ${skeletonColor}`} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })() : fields.length === 0 ? (
        <div className={`text-center py-12 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
          No custom fields found. Click "Add Field" to create one.
        </div>
      ) : (
        <div className={`overflow-x-auto rounded-lg border ${
          theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <table className="w-full">
            <thead className={theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'}>
              <tr>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Field Name
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden md:table-cell ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Field Type
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden lg:table-cell ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Required
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-left text-xs font-medium uppercase tracking-wider hidden sm:table-cell ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Status
                </th>
                <th className={`px-2 sm:px-4 py-2 sm:py-3 text-right text-xs font-medium uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y ${theme === 'dark' ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {fields.map((field) => (
                <tr key={field.id} className={theme === 'dark' ? 'bg-gray-800' : 'bg-white'}>
                  <td className={`px-2 sm:px-4 py-2 sm:py-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    <div>
                      <div className="text-sm sm:text-base font-medium">{field.field_name}</div>
                      {field.description && (
                        <div className={`text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                          {field.description}
                        </div>
                      )}
                      {/* Show type on mobile */}
                      <div className="md:hidden mt-1">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded ${
                          theme === 'dark' ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {getFieldTypeLabel(field.field_type)}
                        </span>
                        {field.is_required && (
                          <span className="ml-1 text-xs text-red-500">Required</span>
                        )}
                      </div>
                      {/* Show status on mobile */}
                      <div className="sm:hidden mt-1">
                        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full ${
                          field.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}>
                          {field.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className={`px-2 sm:px-4 py-2 sm:py-3 hidden md:table-cell ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded ${
                      theme === 'dark' ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'
                    }`}>
                      {getFieldTypeLabel(field.field_type)}
                    </span>
                  </td>
                  <td className={`px-2 sm:px-4 py-2 sm:py-3 hidden lg:table-cell text-xs sm:text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {field.is_required ? 'Yes' : 'No'}
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3 hidden sm:table-cell">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      field.is_active
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                    }`}>
                      {field.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-2 sm:px-4 py-2 sm:py-3">
                    <div className="flex justify-end space-x-1 sm:space-x-2">
                      <button
                        onClick={() => handleEditClick(field)}
                        className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                          theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                        }`}
                        title="Edit"
                      >
                        <Edit size={14} className="sm:w-4 sm:h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleActive(field)}
                        className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-colors ${
                          field.is_active
                            ? 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        title={field.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {field.is_active ? <ToggleRight size={16} className="sm:w-5 sm:h-5" /> : <ToggleLeft size={16} className="sm:w-5 sm:h-5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {isModalOpen && (
        <CustomFieldModal
          field={selectedField}
          onClose={() => setIsModalOpen(false)}
          onSave={handleModalSave}
        />
      )}
    </div>
  );
};

export default CustomFieldsManagement;
